import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { DistributionList, DistributionListMember } from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute, Router } from '@angular/router';
import { get } from 'lodash';
import { DistributionListsResource } from '../../../shared/resources/distribution-lists.resource';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../shared/services/toast.service';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';

interface DistributionListRow extends DatatableRow {
  displayName: string;
  alias: string;
  id: string;
}

@Component({
  selector: 'ziro-distribution-list',
  templateUrl: './distribution-list.component.html',
  styleUrls: ['./distribution-list-component.scss'],
  standalone: false,
})
export class DistributionListComponent implements OnInit, OnDestroy {
  private breadcrumbsService = inject(BreadcrumbsService);
  private bottomNavService = inject(BottomNavService);
  private ziroModalService = inject(ZiroModalService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private distributionListsResource = inject(DistributionListsResource);
  protected router = inject(Router);

  columns: DatatableColumn<DistributionListRow>[];
  tableRows: DistributionListRow[] = [];
  distributionListMember: DistributionListMember[] = [];
  ziroIcons = ZiroIcons;
  isLoading = true;

  private _serverId: string;
  private _distributionListId: number;
  private _subscription = new Subscription();
  private _distributionList: DistributionList;

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  ngOnInit(): void {
    this._serverId = get(this.route, 'snapshot.params.serverid');
    this._distributionListId = get(this.route, 'snapshot.params.distributionlistid');
    this._getDistributionListMembers();
    this._initBottomNav();
  }

  deleteClicked(member: DistributionListMember): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: 'text-danger',
        promptBody: this.translateService.instant('tkey;global.sure.delete.distributionlist.member', {
          userDisplayName: member.displayName,
          userAlias: member.alias,
          listDisplayName: this._distributionList.displayName,
          listAlias: this._distributionList.alias,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._postDeleteMember(member);
            },
          },
        ],
      },
    };

    this.ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe(() => this._refreshList());
  }

  onButtonClicked(): void {
    const url = `server/${this._serverId}/distribution-list/${this._distributionListId}/add`;
    this.router.navigateByUrl(url);
  }

  private _postDeleteMember(memberToDelete: DistributionListMember): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.distributionListsResource
        .removeMemberFromDistributionList(this._serverId, this._distributionListId, memberToDelete.id)
        .subscribe(() => {
          this.toastService.pushDeleteToast('tkey;pages.distributionlist.member', memberToDelete.displayName);
          subscriber.next();
          subscriber.complete();
        });
    });
  }

  private _initBottomNav(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'add-distribution-list-member-button',
          label: 'tkey;pages.distributionlist.add.member',
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'add-distribution-list-member-button',
          state: {
            pending: false,
            buttonDisableState: {
              disabled: false,
              tooltipKey: '',
            },
          },
          icon: this.ziroIcons.ADD,
          type: ButtonTypes.BUTTON,
          cb: () => this.onButtonClicked(),
        },
      ]),
    );
  }

  private _setBreadcrumbs(): void {
    this.breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;search.distribution.lists.section.header' },
      { label: this._distributionList.displayName },
    ]);
  }

  private _refreshList(): void {
    this.distributionListsResource
      .getDistributionList(this._serverId, this._distributionListId)
      .subscribe((distributionList) => {
        this._loadMembers(distributionList);
        this._generateTableRows();
      });
  }

  private _getDistributionListMembers(): void {
    this.isLoading = true;
    this.distributionListsResource
      .getDistributionList(this._serverId, this._distributionListId)
      .subscribe((distributionList) => {
        this._loadMembers(distributionList);
        this._createTable();
        this._generateTableRows();
        this._setBreadcrumbs();
        this.isLoading = false;
      });
  }

  private _loadMembers(distributionList: DistributionList): void {
    this._distributionList = distributionList;
    this.distributionListMember = distributionList.members;
  }

  private _createTable(): void {
    this.columns = [
      {
        name: 'displayName',
        label: 'tkey;pages.distributionlist.display_name',
        columnSize: 'col-sm-6',
      },
      {
        name: 'alias',
        label: 'tkey;pages.distributionlist.alias',
        columnSize: 'col-sm-5',
      },
    ];
  }

  private _generateTableRows(): void {
    this.tableRows = this.distributionListMember.map((member) => {
      return {
        displayName: member.displayName,
        alias: member.alias,
        id: member.id,
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'member-delete',
            icon: ZiroIcons.DELETE_SMALL,
            onClick: () => this.deleteClicked(member),
          },
        ],
      };
    });
  }
}
